<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'w-full sm:max-w-lg' }">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Extend Expiry Date</h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="grid grid-cols-6 gap-x-4 gap-y-6">
                <UFormGroup class="col-span-6" name="expires_at" label="New Expiry Date" required>
                    <UInput v-model="expiryDate" type="date" size="lg" :min="(new Date(subscription.expires_at)).toISOString().split('T')[0]" />
                </UFormGroup>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                    <UButton @click="confirm()" :loading="confirming" type="submit" size="lg" color="black"> Save </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { Subscription } from '~/types/subscription';
import { UserRole } from '~/types/user';

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    subscription?: Subscription
}>()

const toast = useToast()
const auth = useAuthStore()

const confirming = ref<boolean>(false)
const expiryDate = ref<string>()

watch(() => props.open, (open) => {
    if (open) {
        expiryDate.value = props.subscription?.expires_at?.split('T')[0] || ''
    }
})

async function confirm() {
    if (!auth.hasRole(UserRole.admin)) {
        toast.add({
            title: 'You do not have permission to extend subscriptions',
            color: 'red',
            icon: 'i-ph-warning-circle-duotone'
        })

        return dismiss()
    }

    if (!expiryDate.value) {
        toast.add({
            title: 'New expiry date is required.',
            color: 'orange',
            icon: 'i-ph-warning-duotone'
        })
    }

    if (props.subscription?.id) {
        confirming.value = true

        try {
            await $fetch(`/api/subscriptions/${props.subscription.id}`, {
                method: 'PUT',
                body: {
                    expires_at: expiryDate.value
                }
            })

            toast.add({
                title: 'Subscription extended successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            emit('reload')
        } catch (error: any) {
            toast.add({
                title: 'Could not extend subscription!',
                description: 'Please try again later.',
                color: 'red',
                icon: 'i-ph-x-circle-duotone'
            })
        } finally {
            confirming.value = false
            dismiss()
        }
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>
